import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { useValidateIntegrationForm } from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import { useState } from 'react';
import useCreateIntegrationEdgeServer from 'v2/core/repository/edge-server/integrations';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';

type AmazonConnectFormData = {
  instanceARN: string;
  accessURL: string;
  region: string;
  bucketURI: string;
  accessKey: string;
  secretKey: string;
};

const initialState: AmazonConnectFormData = {
  instanceARN: '',
  accessURL: '',
  region: '',
  bucketURI: '',
  accessKey: '',
  secretKey: '',
};

export const AmazonConnectFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
}: GetAddIntegrationFormDrawerContentProps) => {
  const [formData, setFormData] = useState<AmazonConnectFormData>(initialState);
  const [descriptionInput, setDescriptionInput] = useState('');

  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { status, createIntegration } = useCreateIntegrationEdgeServer({
    tenantID,
    direction: IntegrationDirection.Inbound,
    channel: IntegrationChannelType.AmazonConnect,
    integrationType,
    createdByUser: userID,
    description: descriptionInput.trim(),
    inputData: {
      amazonConnect: {
        accessKey: formData.accessKey,
        s3Uri: formData.bucketURI,
        connectAccessURL: formData.accessURL,
        secretKey: formData.secretKey,
        connectInstanceARN: formData.instanceARN,
        region: formData.region,
      },
    },
    onCompleted: () => {
      onDrawerClose();
      trackIntegrationEvent({
        eventName: 'Integration Added',
        integrationChannelType: IntegrationChannelType.AmazonConnect,
      });
    },
  });

  const handleOnChange = (
    eventTaget: EventTarget & (HTMLInputElement | HTMLTextAreaElement),
  ) => {
    const { name, value } = eventTaget;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidated,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.AmazonConnect,
    integrationType,
    hasValidFormFields:
      !!formData.instanceARN &&
      !!formData.accessURL &&
      !!formData.region &&
      !!formData.bucketURI &&
      !!formData.accessKey &&
      !!formData.secretKey,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      await createIntegration();
      trackIntegrationEvent({
        eventName: 'Integration Form Connect CTA Clicked',
        integrationChannelType: IntegrationChannelType.Discourse,
      });
    },
  });

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={2}>
            <TextField
              required
              label="Instance ARN"
              placeholder="Enter Instance ARN"
              onChange={(event) => handleOnChange(event.target)}
              name="instanceARN"
              value={formData.instanceARN}
            />
            <TextField
              required
              label="Access URL"
              placeholder="Enter Access URL"
              onChange={(event) => handleOnChange(event.target)}
              name="accessURL"
              value={formData.accessURL}
            />
            <TextField
              required
              label="AWS Region"
              placeholder="Enter AWS Region"
              onChange={(event) => handleOnChange(event.target)}
              name="region"
              value={formData.region}
            />
            <TextField
              required
              label="AWS Bucket URI"
              placeholder="Enter AWS Bucket URI"
              onChange={(event) => handleOnChange(event.target)}
              name="bucketURI"
              value={formData.bucketURI}
            />
            <TextField
              required
              label="Access Key"
              placeholder="Enter Access Key"
              onChange={(event) => handleOnChange(event.target)}
              name="accessKey"
              value={formData.accessKey}
            />
            <TextField
              required
              label="Secret Key"
              placeholder="Enter Secret Key"
              onChange={(event) => handleOnChange(event.target)}
              name="secretKey"
              value={formData.secretKey}
            />
          </Stack>
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            isValidationInProgress={isValidatingDescription}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isPreValidated={isPreValidated}
            integrationType={integrationType}
          />
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText="Integrate"
        isDisabled={isDisabled}
        isLoading={IsLoading(status)}
      />
    </Stack>
  );
};
